import { useState, useCallback } from "react";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import { generateTaskCode, omitUndefined, type Task, type TaskConfig, type TaskType } from "../lib/taskEngine";

interface CreateTaskOptions {
  teacherId: string;
  teacherName?: string;
  type: TaskType;
  title: string;
  description?: string;
  config: TaskConfig;
  useAudio?: boolean;
  maxAttempts?: number;
  showFeedback?: boolean;
  expiresInHours?: number;
  classroomId?: string;
  assignedStudentIds?: string[];
}

interface TaskGeneratorState {
  isCreating: boolean;
  error: string | null;
  generatedCode: string | null;
}

export function useTaskGenerator() {
  const [state, setState] = useState<TaskGeneratorState>({
    isCreating: false,
    error: null,
    generatedCode: null,
  });

  const createTask = useCallback(async (options: CreateTaskOptions): Promise<string | null> => {
    setState({ isCreating: true, error: null, generatedCode: null });

    try {
      // Generar código único (con retry simple)
      let code = generateTaskCode();
      let attempts = 0;
      const maxRetries = 5;

      // En una app real con alta concurrencia necesitaríamos una Cloud Function
      // para garantizar unicidad. Para este caso, el espacio de códigos es grande
      // (32^6 ≈ 1 billón) así que la probabilidad de colisión es mínima.

      const task: Omit<Task, "createdAt"> & { createdAt: ReturnType<typeof serverTimestamp> } = {
        id: code,
        teacherId: options.teacherId,
        teacherName: options.teacherName || "Profesor",
        type: options.type,
        title: options.title,
        description: options.description || "",
        config: omitUndefined(options.config as Record<string, unknown>) as TaskConfig,
        validation: {
          useAudio: options.useAudio ?? true,
          maxAttempts: options.maxAttempts ?? 10,
          showFeedback: options.showFeedback ?? true,
        },
        createdAt: serverTimestamp(),
        expiresAt: options.expiresInHours
          ? Date.now() + options.expiresInHours * 3600 * 1000
          : undefined,
        classroomId: options.classroomId || undefined,
        assignedStudentIds:
          options.assignedStudentIds?.length ? options.assignedStudentIds : undefined,
      };

      await setDoc(
        doc(db, "bordona_tasks", code),
        omitUndefined(task as unknown as Record<string, unknown>)
      );

      setState({ isCreating: false, error: null, generatedCode: code });
      return code;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Error al crear la tarea";
      setState({ isCreating: false, error: msg, generatedCode: null });
      return null;
    }
  }, []);

  const reset = useCallback(() => {
    setState({ isCreating: false, error: null, generatedCode: null });
  }, []);

  return { state, createTask, reset };
}
